import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import type { KeyboardEvent, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState, ErrorState } from '@/components/feedback/States'
import { Skeleton } from '@/components/ui/Skeleton'
import type { SortDirection } from '@/types/api'
import { cn } from '@/utils/cn'

export interface Column<T> {
  key: string
  header: ReactNode
  cell: (row: T) => ReactNode
  /** API sort key; makes the header a sort button. */
  sortKey?: string
  align?: 'left' | 'right' | 'center'
  /** Hide this column below a breakpoint (the mobile card view still shows what matters). */
  hideBelow?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  className?: string
  headerClassName?: string
}

interface DataTableProps<T> {
  columns: Column<T>[]
  rows: T[] | undefined
  getRowId: (row: T) => string | number
  loading?: boolean
  error?: unknown
  onRetry?: () => void
  sort?: { key: string | undefined; direction: SortDirection }
  onSortChange?: (key: string) => void
  onRowClick?: (row: T) => void
  /** Stacked card rendering used below the `md` breakpoint. */
  mobileCard?: (row: T) => ReactNode
  empty?: ReactNode
  caption: string
  skeletonRows?: number
}

const hideClass = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
  xl: 'hidden xl:table-cell',
  '2xl': 'hidden 2xl:table-cell',
}
const alignClass = { left: 'text-left', right: 'text-right', center: 'text-center' }

/**
 * Accessible data table: semantic <table> with sortable headers (aria-sort), skeleton loading,
 * error and empty states, and a card layout on small screens.
 */
export function DataTable<T>({
  columns,
  rows,
  getRowId,
  loading,
  error,
  onRetry,
  sort,
  onSortChange,
  onRowClick,
  mobileCard,
  empty,
  caption,
  skeletonRows = 8,
}: DataTableProps<T>) {
  const { t } = useTranslation()
  if (error && !loading) return <ErrorState onRetry={onRetry} compact />

  const isEmpty = !loading && (rows?.length ?? 0) === 0
  const handleKey = (e: KeyboardEvent<HTMLTableRowElement>, row: T) => {
    if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      onRowClick(row)
    }
  }

  return (
    <div>
      {/* Card list for phones */}
      {mobileCard ? (
        <ul className="divide-y divide-line md:hidden" aria-label={caption}>
          {loading
            ? Array.from({ length: 5 }, (_, i) => (
                <li key={i} className="flex items-center gap-3 p-4">
                  <Skeleton className="size-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-3.5 w-1/2" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                </li>
              ))
            : rows?.map((row) => (
                <li key={getRowId(row)}>
                  {onRowClick ? (
                    <button
                      type="button"
                      onClick={() => onRowClick(row)}
                      className="block w-full p-4 text-left transition-colors hover:bg-surface-muted"
                    >
                      {mobileCard(row)}
                    </button>
                  ) : (
                    <div className="p-4">{mobileCard(row)}</div>
                  )}
                </li>
              ))}
        </ul>
      ) : null}

      <div className={cn('scrollbar-thin overflow-x-auto', mobileCard && 'hidden md:block')}>
        <table className="w-full border-separate border-spacing-0 text-sm">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>
              {columns.map((col) => {
                const active = sort?.key !== undefined && sort.key === col.sortKey
                const ariaSort = active
                  ? sort.direction === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : col.sortKey
                    ? 'none'
                    : undefined
                return (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={ariaSort}
                    className={cn(
                      'sticky top-0 border-b border-line bg-surface-muted px-4 py-2.5 text-2xs font-semibold tracking-wider whitespace-nowrap text-muted uppercase first:pl-5 last:pr-5',
                      alignClass[col.align ?? 'left'],
                      col.hideBelow && hideClass[col.hideBelow],
                      col.headerClassName,
                    )}
                  >
                    {col.sortKey && onSortChange ? (
                      <button
                        type="button"
                        onClick={() => {
                          if (col.sortKey) onSortChange(col.sortKey)
                        }}
                        className={cn(
                          '-mx-1 inline-flex items-center gap-1 rounded-xs px-1 py-0.5 uppercase hover:text-ink',
                          active && 'text-brand-700',
                        )}
                      >
                        {col.header}
                        {active ? (
                          sort.direction === 'asc' ? (
                            <ArrowUp className="size-3" aria-hidden="true" />
                          ) : (
                            <ArrowDown className="size-3" aria-hidden="true" />
                          )
                        ) : (
                          <ArrowUpDown className="size-3 opacity-40" aria-hidden="true" />
                        )}
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: skeletonRows }, (_, i) => (
                  <tr key={i}>
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={cn(
                          'border-b border-line px-4 py-3.5 first:pl-5 last:pr-5',
                          col.hideBelow && hideClass[col.hideBelow],
                        )}
                      >
                        <Skeleton className="h-3.5 w-3/4" />
                      </td>
                    ))}
                  </tr>
                ))
              : rows?.map((row) => (
                  <tr
                    key={getRowId(row)}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    onKeyDown={onRowClick ? (e) => handleKey(e, row) : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    className={cn(
                      'group transition-colors',
                      onRowClick &&
                        'cursor-pointer hover:bg-brand-50/40 focus-visible:bg-brand-50/60 focus-visible:outline-none',
                    )}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={cn(
                          'border-b border-line px-4 py-3 align-middle text-ink-soft group-last:border-b-0 first:pl-5 last:pr-5',
                          alignClass[col.align ?? 'left'],
                          col.hideBelow && hideClass[col.hideBelow],
                          col.className,
                        )}
                      >
                        {col.cell(row)}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {isEmpty
        ? (empty ?? (
            <EmptyState
              title={t('Nothing here yet')}
              description={t('No records match the current filters.')}
              compact
            />
          ))
        : null}
    </div>
  )
}
